/**
 * Merch PDP — two-tier (Colour x Size) variant selection, gallery filtering, and add to cart.
 *
 * Standalone by design. It never touches ProductFormStore or pdp-new-product-form.js: that
 * system applies an ungated volume-discount ladder (assets/pdp-new-product-form.js:232) and
 * stamps properties.item_total on every cart line, both of which are wrong for merch.
 *
 * Add to cart goes through window.CartDrawer.update(), the documented mutation choke point in
 * assets/cart-drawer.js, which owns the POST, the drawer re-render, the header discount bar,
 * the loading state, and the cart:updating/updated/error events.
 *
 * Public API on `window.MerchPDP` (mirrors window.CartProgress in site-discount-bar.js):
 *
 *   Lifecycle
 *     hydrate(scope)        -> mount every un-mounted [data-merch-pdp] in scope (default document)
 *     destroyWithin(scope)  -> tear down the instances inside scope
 *     instances             -> Map<instanceId, MerchPDPApp>
 *     get(instanceId)       -> one instance (the only/first one when called with no id)
 *
 *   State
 *     getState(instanceId)  -> { instanceId, productId, color, size, variantId, available }
 *     select(patch, opts)   -> set colour and/or size through the same path a click takes,
 *                              e.g. MerchPDP.select({ color: 'Navy' }). Pass { silent: true }
 *                              to suppress the merch:select event.
 *     addToCart(instanceId) -> Promise, the same call the CTA makes
 *
 *   Events (dispatched on document, bubbling) — the seam for Convert experiments
 *     merch:ready       detail { instanceId, productId, state }
 *     merch:select      detail { instanceId, productId, state, previous }
 *     merch:atc         detail { instanceId, productId, variantId, state }
 *                       fires on ATTEMPT, before the cart POST resolves — a failed add fires
 *                       this and then merch:atc:error. Bind conversion goals to CartDrawer's
 *                       cart:updated instead, or this will over-count.
 *     merch:atc:error   detail { instanceId, productId, variantId, error }
 *
 * A Convert variation that rewrites the PDP markup should call
 * MerchPDP.destroyWithin(el) then MerchPDP.hydrate(el) — mounting is idempotent, so a bare
 * hydrate() is safe to call at any time and will skip anything already mounted.
 */
(function () {
    'use strict';

    const SELECTOR = '[data-merch-pdp]';

    let instanceCounter = 0;

    class MerchPDPApp {
        constructor(root) {
            this.root = root;
            this.initialized = false;

            const dataEl = root.querySelector('[data-merch-pdp-data]');
            if (!dataEl) return;
            this.dataEl = dataEl;

            let data;
            try {
                data = JSON.parse(dataEl.textContent);
            } catch (error) {
                console.error('[merch-pdp] could not parse variant data', error);
                return;
            }

            this.variants = data.variants || [];
            if (this.variants.length === 0) return;

            this.instanceId = `merch-pdp-${++instanceCounter}`;
            this.productId = root.dataset.productId || null;
            this.controller = new AbortController();

            this.cacheElements();
            this.seedSelection();
            this.bindEvents();
            this.render();

            this.initialized = true;
            this.emit('merch:ready', { state: this.getState() });
        }

        // ---- setup ----

        cacheElements() {
            const root = this.root;

            this.colorButtons = Array.from(root.querySelectorAll('[data-merch-color-option]'));
            this.sizeButtons = Array.from(root.querySelectorAll('[data-merch-size-option]'));

            this.priceEl = root.querySelector('[data-merch-price]');
            this.colorLabel = root.querySelector('[data-merch-selected-color]');
            this.sizeLabel = root.querySelector('[data-merch-selected-size]');
            this.atc = root.querySelector('[data-merch-atc]');
            // Label lives in its own span so writing to it doesn't wipe the button's arrow icon.
            this.atcText = root.querySelector('[data-merch-atc-text]');
            this.errorEl = root.querySelector('[data-merch-error]');

            this.gallery = root.querySelector('[data-merch-gallery]');
            this.track = root.querySelector('[data-merch-gallery-track]');
            this.dotsEl = root.querySelector('[data-merch-gallery-dots]');
            this.prevArrow = root.querySelector('[data-merch-gallery-prev]');
            this.nextArrow = root.querySelector('[data-merch-gallery-next]');

            // The slides for the ACTIVE colour, not every slide in the track — the arrows and
            // the dots both act on this list.
            this.visibleSlides = [];
            this.activeIndex = 0;
            this.observer = null;

            // The colour the gallery is currently filtered to. render() runs on every
            // interaction, but re-filtering rewinds the track to slide 1 — so the gallery is
            // only rebuilt when the COLOUR actually changed. See renderGallery().
            this.renderedColor = null;
            this.galleryRendered = false;
            this.dotsController = null;
        }

        seedSelection() {
            // Honour ?variant= so the swatch tapped on the PLP is the one that opens here.
            // Falls back to the first available variant, so a product whose first colour is
            // sold out doesn't open in an unbuyable state.
            const requestedId = parseInt(new URLSearchParams(window.location.search).get('variant'), 10);
            const requested = this.variants.find((v) => v.id === requestedId);
            const first = requested || this.variants.find((v) => v.available) || this.variants[0];

            this.selected = { color: first.color, size: first.size };
        }

        bindEvents() {
            const { signal } = this.controller;

            this.colorButtons.forEach((button) => {
                button.addEventListener('click', () => this.select({ color: button.dataset.value }), { signal });
            });

            this.sizeButtons.forEach((button) => {
                button.addEventListener('click', () => {
                    if (button.disabled) return;
                    this.select({ size: button.dataset.value });
                }, { signal });
            });

            if (this.prevArrow) {
                this.prevArrow.addEventListener('click', () => this.goTo(this.activeIndex - 1), { signal });
            }

            if (this.nextArrow) {
                this.nextArrow.addEventListener('click', () => this.goTo(this.activeIndex + 1), { signal });
            }

            if (this.atc) {
                this.atc.addEventListener('click', () => this.addToCart(), { signal });
            }
        }

        // A Convert variation that rewrites the root's innerHTML leaves the mount marker in
        // place while detaching every node we cached, so the marker alone is not proof the
        // instance is still wired to the document.
        isMounted() {
            return !!(this.dataEl && this.root.contains(this.dataEl));
        }

        destroy() {
            if (this.controller) this.controller.abort();
            if (this.dotsController) this.dotsController.abort();
            if (this.observer) this.observer.disconnect();
            this.observer = null;
            this.initialized = false;
            delete this.root.dataset.merchPdpInstance;
        }

        // ---- events ----

        emit(name, detail) {
            document.dispatchEvent(new CustomEvent(name, {
                bubbles: true,
                detail: Object.assign({ instanceId: this.instanceId, productId: this.productId }, detail),
            }));
        }

        // ---- resolution ----

        findVariant(color, size) {
            return this.variants.find((v) => v.color === color && v.size === size) || null;
        }

        sizeIsAvailable(color, size) {
            const variant = this.findVariant(color, size);
            return !!(variant && variant.available);
        }

        getState() {
            const variant = this.findVariant(this.selected.color, this.selected.size);
            return {
                instanceId: this.instanceId,
                productId: this.productId,
                color: this.selected.color,
                size: this.selected.size,
                variantId: variant ? variant.id : null,
                available: !!(variant && variant.available),
            };
        }

        /**
         * The single selection path — every click, and every programmatic call from a Convert
         * variation, goes through here.
         */
        select(patch = {}, { silent = false } = {}) {
            const previous = this.getState();

            if ('color' in patch && patch.color !== this.selected.color) {
                this.selected.color = patch.color;

                // Keep the chosen size if this colour has it; otherwise clear it and make the
                // shopper choose again rather than silently switching them to another size.
                if (this.selected.size && !this.sizeIsAvailable(this.selected.color, this.selected.size)) {
                    this.selected.size = null;
                }
            }

            if ('size' in patch) {
                this.selected.size = patch.size;
            }

            this.render();

            const state = this.getState();
            if (!silent) this.emit('merch:select', { state, previous });
            return state;
        }

        // ---- gallery ----

        renderGallery() {
            if (!this.gallery || !this.track) return;

            // Rebuilding rewinds the track, so only do it when the colour actually changed.
            // Without this guard a size click or a finished add-to-cart throws the shopper
            // back to slide 1.
            if (this.galleryRendered && this.renderedColor === this.selected.color) return;

            this.galleryRendered = true;
            this.renderedColor = this.selected.color;

            this.gallery.setAttribute('data-active-color', this.selected.color || '');

            const slides = Array.from(this.track.querySelectorAll('[data-merch-gallery-slide]'));
            const visible = [];

            slides.forEach((slide) => {
                // Slides with no data-color belong to no particular colour (lifestyle shots,
                // or every slide on a product with no Color option) and always show.
                const slideColor = slide.dataset.color;
                const show = !slideColor || !this.selected.color || slideColor === this.selected.color;
                slide.classList.toggle('is-hidden', !show);
                if (show) visible.push(slide);
            });

            this.visibleSlides = visible;
            this.activeIndex = 0;

            this.track.scrollTo({ left: 0, behavior: 'auto' });
            this.renderDots();
            this.observeSlides();
            this.syncControls();
        }

        goTo(index) {
            const slide = this.visibleSlides[index];
            if (!slide) return;
            this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        }

        renderDots() {
            if (!this.dotsEl) return;

            // Dots are thrown away and rebuilt on every colour change. Registering their
            // listeners on the instance-wide signal would keep a reference to every dot ever
            // created alive until destroy(), so they get their own controller.
            if (this.dotsController) this.dotsController.abort();
            this.dotsController = new AbortController();

            this.dotsEl.textContent = '';
            // One shot for this colour needs no pagination. :empty hides the rail.
            if (this.visibleSlides.length < 2) return;

            const { signal } = this.dotsController;

            this.visibleSlides.forEach((slide, index) => {
                const dot = document.createElement('button');
                dot.type = 'button';
                dot.className = `merch-gallery__dot${index === 0 ? ' is-active' : ''}`;
                dot.setAttribute('aria-label', `Go to image ${index + 1}`);
                dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
                dot.addEventListener('click', () => this.goTo(index), { signal });
                this.dotsEl.appendChild(dot);
            });
        }

        // Reflects activeIndex onto the dots and the arrows. Arrows disappear entirely
        // for a single-slide colour rather than sitting there permanently disabled.
        syncControls() {
            Array.from(this.dotsEl ? this.dotsEl.children : []).forEach((dot, i) => {
                const active = i === this.activeIndex;
                dot.classList.toggle('is-active', active);
                dot.setAttribute('aria-current', active ? 'true' : 'false');
            });

            const multiple = this.visibleSlides.length > 1;
            if (this.prevArrow) {
                this.prevArrow.hidden = !multiple;
                this.prevArrow.disabled = this.activeIndex <= 0;
            }
            if (this.nextArrow) {
                this.nextArrow.hidden = !multiple;
                this.nextArrow.disabled = this.activeIndex >= this.visibleSlides.length - 1;
            }
        }

        observeSlides() {
            if (this.observer) this.observer.disconnect();

            this.observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;
                        this.activeIndex = this.visibleSlides.indexOf(entry.target);
                        this.syncControls();
                    });
                },
                { root: this.track, threshold: 0.6 }
            );

            this.visibleSlides.forEach((slide) => this.observer.observe(slide));
        }

        // ---- render ----

        setAtcLabel(text) {
            if (this.atcText) this.atcText.textContent = text;
        }

        showError(message) {
            if (!this.errorEl) return;
            this.errorEl.textContent = message;
            this.errorEl.hidden = false;
        }

        clearError() {
            if (!this.errorEl) return;
            this.errorEl.textContent = '';
            this.errorEl.hidden = true;
        }

        render() {
            this.colorButtons.forEach((button) => {
                const isSelected = button.dataset.value === this.selected.color;
                button.classList.toggle('is-selected', isSelected);
                button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
            });

            this.sizeButtons.forEach((button) => {
                const size = button.dataset.value;
                const available = this.sizeIsAvailable(this.selected.color, size);
                const isSelected = size === this.selected.size;

                button.disabled = !available;
                button.classList.toggle('is-selected', isSelected && available);
                button.setAttribute('aria-pressed', isSelected && available ? 'true' : 'false');
            });

            if (this.colorLabel && this.selected.color) this.colorLabel.textContent = this.selected.color;
            if (this.sizeLabel) this.sizeLabel.textContent = this.selected.size || '';

            const variant = this.findVariant(this.selected.color, this.selected.size);

            if (this.priceEl && variant) this.priceEl.textContent = variant.priceFormatted;

            if (this.atc) {
                const buyable = !!(variant && variant.available);
                this.atc.disabled = !buyable;
                if (!variant && this.sizeButtons.length > 0) {
                    this.setAtcLabel('Select a size');
                } else if (!buyable) {
                    this.setAtcLabel('Sold out');
                } else {
                    this.setAtcLabel(this.atc.dataset.defaultLabel);
                }
            }

            this.renderGallery();
        }

        // ---- add to cart ----

        addToCart() {
            this.clearError();

            const variant = this.findVariant(this.selected.color, this.selected.size);
            if (!variant || !variant.available) return Promise.resolve(null);

            if (!window.CartDrawer || typeof window.CartDrawer.update !== 'function') {
                this.showError('Cart is unavailable. Please refresh and try again.');
                return Promise.resolve(null);
            }

            if (this.atc) this.atc.disabled = true;
            this.setAtcLabel('Adding...');
            this.emit('merch:atc', { variantId: variant.id, state: this.getState() });

            // No properties.item_total here — merch stays out of the volume-discount
            // Function that the pdp-new form feeds.
            return window.CartDrawer
                .update([{
                    path: 'cart/add.js',
                    body: {
                        items: [{
                            id: variant.id,
                            quantity: 1,
                            properties: { '_is_merch_product': true },
                        }],
                    },
                }], {
                    source: 'merch-pdp',
                })
                .then((cart) => {
                    const trigger = document.querySelector('[data-drawer-name="cart"]');
                    if (trigger) trigger.click();
                    return cart;
                })
                .catch((error) => {
                    this.showError('Sorry, we could not add that to your cart.');
                    this.emit('merch:atc:error', { variantId: variant.id, error });
                    return null;
                })
                .then((result) => {
                    if (this.atc) this.atc.disabled = false;
                    this.render();
                    return result;
                });
        }
    }

    const MerchPDPManager = {
        apps: new Map(),

        hydrate(scope = document) {
            const roots = scope.querySelectorAll ? Array.from(scope.querySelectorAll(SELECTOR)) : [];
            if (scope.matches && scope.matches(SELECTOR)) roots.unshift(scope);

            roots.forEach((root) => {
                const existingId = root.dataset.merchPdpInstance;
                if (existingId) {
                    const existing = MerchPDPManager.apps.get(existingId);
                    // Still wired to the document — nothing to do. Otherwise the markup was
                    // replaced underneath us, so tear the stale instance down and re-mount
                    // rather than silently no-oping and leaving a dead PDP.
                    if (existing && existing.isMounted()) return;
                    MerchPDPManager.destroyInstance(existingId);
                }

                const app = new MerchPDPApp(root);
                if (!app.initialized) return;

                root.dataset.merchPdpInstance = app.instanceId;
                MerchPDPManager.apps.set(app.instanceId, app);
            });
        },

        destroyWithin(scope = document) {
            const roots = scope.querySelectorAll ? Array.from(scope.querySelectorAll(SELECTOR)) : [];
            if (scope.matches && scope.matches(SELECTOR)) roots.unshift(scope);

            roots.forEach((root) => {
                const instanceId = root.dataset.merchPdpInstance;
                if (instanceId) MerchPDPManager.destroyInstance(instanceId);
            });
        },

        destroyInstance(instanceId) {
            const app = MerchPDPManager.apps.get(instanceId);
            if (!app) return;
            app.destroy();
            MerchPDPManager.apps.delete(instanceId);
        },

        // With no id, return the only instance — there is one PDP per page, so a Convert
        // variation should never have to know the generated id.
        get(instanceId) {
            if (instanceId) return MerchPDPManager.apps.get(instanceId) || null;
            return MerchPDPManager.apps.values().next().value || null;
        },
    };

    function ready(callback) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback, { once: true });
            return;
        }
        callback();
    }

    ready(() => MerchPDPManager.hydrate(document));

    document.addEventListener('shopify:section:load', (event) => {
        if (event && event.target) MerchPDPManager.hydrate(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
        if (event && event.target) MerchPDPManager.destroyWithin(event.target);
    });

    const facade = window.MerchPDP || {};
    facade.hydrate = (scope) => MerchPDPManager.hydrate(scope || document);
    facade.destroyWithin = (scope) => MerchPDPManager.destroyWithin(scope || document);
    facade.get = (instanceId) => MerchPDPManager.get(instanceId);
    facade.getState = (instanceId) => {
        const app = MerchPDPManager.get(instanceId);
        return app ? app.getState() : null;
    };
    facade.select = (patch, options) => {
        const app = MerchPDPManager.get(options && options.instanceId);
        return app ? app.select(patch, options) : null;
    };
    facade.addToCart = (instanceId) => {
        const app = MerchPDPManager.get(instanceId);
        return app ? app.addToCart() : Promise.resolve(null);
    };
    facade.instances = MerchPDPManager.apps;
    window.MerchPDP = facade;
})();
